/**
 * ENVIRONMENT CONFIG — the only place the app reads deployment-specific
 * values from. Every value comes from Vite env vars (import.meta.env),
 * never hardcoded. This is what "no localhost/file:// hardcoding" means
 * concretely: nothing below is a literal URL.
 *
 * Vite only exposes vars prefixed VITE_ to client code — see .env.example.
 */

export type AppEnvironment = 'development' | 'preview' | 'production';

function readEnvironment(): AppEnvironment {
  const raw = (import.meta.env.VITE_ENVIRONMENT ?? '').toLowerCase();
  if (raw === 'production' || raw === 'preview' || raw === 'development') return raw;
  // Honest default: an unset/unknown value never silently claims "production".
  return import.meta.env.PROD ? 'preview' : 'development';
}

export const ENV = {
  /** development | preview | production — drives the Topbar environment badge */
  environment: readEnvironment(),
  /**
   * The app's own public origin, e.g. https://command.ugarits.com.
   * Falls back to window.location.origin at runtime so nothing breaks
   * before this is configured — but any internal "copy link" feature
   * should prefer this value once set, per the "linkability" requirement.
   */
  appOrigin: import.meta.env.VITE_APP_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : ''),
  /**
   * Base URL for a future backend API. Empty string today because no
   * backend exists yet — every dataService/connector call must treat an
   * empty apiBaseUrl as "backend not connected" and say so, never fail
   * silently or fabricate a response.
   */
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '',
  /** app version shown in Settings/System, read from package.json at build time via Vite define — see vite.config.ts */
  version: __APP_VERSION__,
  buildTime: __BUILD_TIME__,
} as const;

export function isBackendConnected(): boolean {
  return ENV.apiBaseUrl.length > 0;
}
